import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Eye,
  Compass,
  Award,
  ShieldCheck,
  Clock,
  Users,
  Sparkles,
  TrendingUp,
  HeartHandshake,
  CheckCircle2,
  Phone,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import Reveal from "@/components/site/Reveal";
import AnimatedCounter from "@/components/site/AnimatedCounter";

const philosophy = [
  {
    icon: Target,
    label: "Our Mission",
    title: "Make compliance quietly reliable.",
    body:
      "We take the friction out of tax, accounting and regulatory work so founders can spend their time on growth — not chasing deadlines.",
  },
  {
    icon: Eye,
    label: "Our Vision",
    title: "The most trusted advisory in Western India.",
    body:
      "To be the firm that growth-stage businesses in Gujarat and beyond turn to first — known for discipline, clarity and long-term relationships.",
  },
  {
    icon: Compass,
    label: "Our Philosophy",
    title: "Advise. Assist. Achieve.",
    body:
      "We believe advisory is a craft. Understand the business deeply, work alongside the owner, and measure success by the outcomes — not the invoices.",
  },
];

const values = [
  { icon: Award, title: "Expertise", desc: "Practical depth across tax, accounting, finance and compliance." },
  { icon: ShieldCheck, title: "Integrity", desc: "Confidentiality, independence and an uncompromising ethical bar." },
  { icon: Clock, title: "Reliability", desc: "Predictable delivery — the same rhythm, month after month." },
  { icon: Users, title: "Partnership", desc: "We work as an extension of your team, not a vendor on the side." },
  { icon: Sparkles, title: "Clarity", desc: "Plain language. Clear documents. No hidden disclaimers." },
  { icon: TrendingUp, title: "Outcome Focus", desc: "We measure ourselves on what actually moves your business." },
];

const milestones = [
  { year: "Founded", title: "Avvanta Advisory established in Surat", desc: "A focused practice built around structured compliance and practical advisory." },
  { year: "Year 1", title: "Multi-industry client base", desc: "Onboarded businesses across textile, trading, services and e-commerce." },
  { year: "Year 2", title: "Cross-border services launched", desc: "Expanded to support US accounting, taxation and payroll for Indian-owned businesses." },
  { year: "Today", title: "50+ businesses, 100+ filings", desc: "Trusted advisor to founders who value discipline, clarity and long-term partnership." },
];

const highlights = [
  { k: "50+", l: "Businesses Served" },
  { k: "100+", l: "Filings Completed" },
  { k: "4", l: "Core Practice Areas" },
  { k: "6", l: "Specialized Service Lines" },
];

const approach = [
  "Deep discovery before we recommend anything",
  "One accountable relationship partner per client",
  "Checklist-driven execution across every service line",
  "Monthly reviews with leadership — not just month-end filings",
  "Clear, written scopes of work and timelines",
  "Proactive alerts on deadlines, notices and regulatory changes",
];

export default function About() {
  return (
    <>
      {/* HEADER */}
      <section data-testid="about-header" className="page-offset bg-[#F8F9FA] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.35] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(10,31,68,0.08) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <Reveal>
            <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
              About Avvanta
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display font-light text-[#0A1F44] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Your growth. Our expertise.{" "}
              <span className="italic">Together we achieve.</span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-8 h-[2px] w-14 bg-[#D4AF37] mx-auto" />
            <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
              Advise · Assist · Achieve — the three principles that shape how we work with every
              business we serve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal y={28}>
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1758518727077-ffb66ffccced?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBpbiUyMG1lZXRpbmd8ZW58MHx8fHwxNzc3ODg4MDYzfDA&ixlib=rb-4.1.0&q=85"
                  alt="Business professionals in meeting"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] hover:scale-[1.03]"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 hidden md:flex bg-[#0A1F44] text-white p-6 w-56 shadow-[0_20px_50px_-20px_rgba(10,31,68,0.45)]">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Based in</div>
                  <div className="mt-2 font-display text-2xl">Surat, Gujarat</div>
                  <div className="text-white/70 text-[13px] mt-1">Serving locally, supporting globally.</div>
                </div>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <SectionHeading
                eyebrow="Our Story"
                title="A practical advisory built for modern Indian businesses."
                description="Avvanta Advisory helps founders and finance teams stay compliant, organized and growth-ready. We believe advisory should be direct, clear and actionable — not buried in jargon or lost in delays."
              />
            </Reveal>
            <Reveal delay={100}>
              <p className="mt-6 text-[15px] text-[#4B5563] leading-relaxed">
                From GST and income tax to accounting, registrations and project finance — our team
                works as an extension of yours. We bring structure, discipline and a genuine care for
                the outcome on your P&amp;L. Every engagement is anchored by a single point of contact,
                clear scoping, and monthly cadence so nothing falls through the cracks.
              </p>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 text-[15px] text-[#4B5563] leading-relaxed">
                Our clients range from first-time founders to multi-entity groups — across textile,
                trading, services, e-commerce and import-export. What unites them is a preference for
                advisors who are accountable, precise and available.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "Advise", d: "clarity first" },
                { k: "Assist", d: "hands-on" },
                { k: "Achieve", d: "results" },
              ].map((x, i) => (
                <Reveal key={x.k} delay={200 + i * 80}>
                  <div className="border-t border-[#E5E7EB] pt-4">
                    <div className="font-display text-[#0A1F44] text-xl">{x.k}</div>
                    <div className="text-[12px] uppercase tracking-widest text-[#D4AF37] mt-1">
                      {x.d}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BY THE NUMBERS */}
      <section data-testid="about-metrics" className="py-14 md:py-16 bg-[#0A1F44] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-8">
          {highlights.map((h, i) => (
            <Reveal key={h.l} delay={i * 80}>
              <div className="relative pl-6 md:pl-8">
                <span className="absolute left-0 top-1 bottom-1 w-[3px] bg-[#D4AF37]" aria-hidden="true" />
                <div className="font-display text-3xl md:text-4xl lg:text-[40px] font-medium tracking-tight leading-none text-white">
                  <AnimatedCounter value={h.k} />
                </div>
                <div className="mt-3 text-[11px] md:text-[12px] uppercase tracking-[0.22em] text-[#D4AF37]/90 font-semibold">
                  {h.l}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="about-philosophy" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="Mission · Vision · Philosophy"
              title="How we think about our craft."
              align="center"
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {philosophy.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.label} delay={i * 120}>
                  <div
                    data-testid={`philosophy-${i}`}
                    className="group relative bg-[#F8F9FA] border border-gray-200 p-8 md:p-10 h-full transition-all duration-500 hover:bg-[#0A1F44] hover:border-[#0A1F44]"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-white border border-[#D4AF37] text-[#0A1F44] group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] group-hover:text-[#0A1F44] transition-colors">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <div className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                      {p.label}
                    </div>
                    <h3 className="mt-2 font-display text-xl md:text-2xl text-[#0A1F44] group-hover:text-white font-medium transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-[14px] text-[#4B5563] group-hover:text-white/75 leading-relaxed transition-colors">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section data-testid="about-approach" className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <SectionHeading
              eyebrow="Our Approach"
              title="Six operating principles that keep us consistent."
              description="These are the habits we apply across every engagement — the reason clients describe working with us as 'quietly reliable'."
            />
          </Reveal>
          <div className="space-y-4">
            {approach.map((a, i) => (
              <Reveal key={a} delay={i * 70}>
                <div
                  data-testid={`approach-${i}`}
                  className="group flex items-start gap-4 bg-white border border-gray-200 p-5 md:p-6 hover:border-[#D4AF37] hover:translate-x-1 transition-all duration-300"
                >
                  <div className="shrink-0 w-8 h-8 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A1F44] transition-colors">
                    <CheckCircle2 size={16} strokeWidth={2} />
                  </div>
                  <p className="text-[15px] text-[#111827] leading-relaxed pt-1">{a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="about-values" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="What We Stand For"
              title="Six values that guide every decision."
              align="center"
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div
                    data-testid={`value-${i}`}
                    className="bg-white border border-gray-200 p-8 group hover:border-[#D4AF37] hover:-translate-y-1 transition-all duration-500"
                  >
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0A1F44] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A1F44] transition-colors">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-display text-xl text-[#0A1F44] font-medium">{v.title}</h3>
                    <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section data-testid="about-journey" className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <SectionHeading
              eyebrow="Our Journey"
              title="Milestones that shaped the firm."
              align="center"
            />
          </Reveal>
          <div className="mt-16 relative">
            <div
              aria-hidden="true"
              className="absolute left-[11px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/40 to-transparent md:-translate-x-1/2"
            />
            <div className="space-y-10 md:space-y-14">
              {milestones.map((m, i) => {
                const alignLeft = i % 2 === 0;
                return (
                  <Reveal key={m.year} delay={i * 120}>
                    <div
                      data-testid={`milestone-${i}`}
                      className={`relative md:grid md:grid-cols-2 md:gap-12 items-start ${
                        alignLeft ? "" : "md:[&>div:first-child]:col-start-2"
                      }`}
                    >
                      <div className="pl-10 md:pl-0 md:pr-0">
                        <div
                          className={`relative bg-white border border-gray-200 p-6 md:p-8 ${
                            alignLeft ? "md:mr-8 md:text-right" : "md:ml-8"
                          }`}
                        >
                          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
                            {m.year}
                          </span>
                          <h3 className="mt-2 font-display text-lg md:text-xl text-[#0A1F44] font-medium">
                            {m.title}
                          </h3>
                          <p className="mt-3 text-[14px] text-[#4B5563] leading-relaxed">{m.desc}</p>
                        </div>
                      </div>
                      {/* dot */}
                      <span
                        aria-hidden="true"
                        className="absolute left-0 md:left-1/2 top-7 w-[22px] h-[22px] rounded-full bg-[#0A1F44] border-[3px] border-[#D4AF37] md:-translate-x-1/2"
                      />
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE / TEAM VOICE */}
      <section data-testid="about-promise" className="relative py-24 md:py-32 bg-[#0A1F44] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1554224154-22dec7ec8818?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHx0YXglMjBjb21wbGlhbmNlJTIwYnVzaW5lc3MlMjBkb2N1bWVudHN8ZW58MHx8fHwxNzc3ODg4MDY4fDA&ixlib=rb-4.1.0&q=85')",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <HeartHandshake size={36} strokeWidth={1.25} className="text-[#D4AF37] mx-auto" />
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-6 font-display text-3xl md:text-4xl lg:text-[48px] leading-[1.15] tracking-tight">
              A firm is judged by its worst month —{" "}
              <span className="text-[#D4AF37] italic font-light">not its best.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-white/75 max-w-2xl mx-auto text-[15px] md:text-base leading-relaxed">
              That is why we design every engagement for the tough months — the audit, the notice,
              the lender question, the late document. When those moments come, you should feel
              supported, not scrambling.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative bg-[#F8F9FA] border border-gray-200 p-10 md:p-16 overflow-hidden">
              <span className="absolute top-0 left-0 h-full w-[3px] bg-[#D4AF37]" aria-hidden="true" />
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
                <div className="lg:col-span-3">
                  <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
                    Let's Talk
                  </span>
                  <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-[44px] leading-[1.1] text-[#0A1F44] tracking-tight">
                    Let's build a compliant, confident business —{" "}
                    <span className="italic font-light">together.</span>
                  </h2>
                  <p className="mt-5 text-[15px] md:text-base text-[#4B5563] leading-relaxed max-w-2xl">
                    Tell us a bit about where you are — we will respond within one business day with
                    a structured next step.
                  </p>
                </div>
                <div className="lg:col-span-2 flex flex-col gap-4 lg:items-end">
                  <Link
                    to="/contact"
                    data-testid="about-cta"
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-3 bg-[#0A1F44] text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors"
                  >
                    Talk to Our Team <ArrowRight size={18} />
                  </Link>
                  <a
                    href="tel:+917487978020"
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-3 border border-[#0A1F44] text-[#0A1F44] px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                  >
                    <Phone size={16} strokeWidth={1.75} /> +91 74879 78020
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
